import { z } from 'zod';

export const REACTION_TYPES = [
  'LIKE',
  'DISLIKE',
  'LAUGH',
  'CELEBRATE',
  'CONFUSED',
  'LOVE',
  'SHIP_IT',
  'SEEN',
] as const;
export const REACTION_SUBJECT_KEY_VERSION = 'v1';
export const REACTION_SUBJECT_KEY_MAX_LENGTH = 255;
export const REACTION_DOMAIN_NAMESPACE_MAX_LENGTH = 64;
export const REACTION_TARGET_ID_MAX_LENGTH =
  REACTION_SUBJECT_KEY_MAX_LENGTH -
  REACTION_DOMAIN_NAMESPACE_MAX_LENGTH -
  `:${REACTION_SUBJECT_KEY_VERSION}:`.length;
export const REACTION_IDEMPOTENCY_KEY_MAX_LENGTH = 128;
export const REACTION_ACTOR_DISPLAY_LABEL_MAX_LENGTH = 255;
export const REACTION_ACTOR_CONTEXT_LABEL_MAX_LENGTH = 255;
export const REACTION_ACTOR_PREVIEW_MAX = 20;

const REACTION_DOMAIN_NAMESPACE_PATTERN =
  /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/;
const REACTION_TARGET_ID_PATTERN =
  /^[A-Za-z0-9](?:[A-Za-z0-9._-]*[A-Za-z0-9])?$/;

export const ReactionTypeSchema = z.enum(REACTION_TYPES);
export type ReactionType = z.infer<typeof ReactionTypeSchema>;

export const ReactionDomainNamespaceSchema = z
  .string()
  .min(1)
  .max(REACTION_DOMAIN_NAMESPACE_MAX_LENGTH)
  .regex(REACTION_DOMAIN_NAMESPACE_PATTERN);
export type ReactionDomainNamespace = z.infer<
  typeof ReactionDomainNamespaceSchema
>;

export const ReactionTargetIdSchema = z
  .string()
  .min(1)
  .max(REACTION_TARGET_ID_MAX_LENGTH)
  .regex(REACTION_TARGET_ID_PATTERN);
export type ReactionTargetId = z.infer<typeof ReactionTargetIdSchema>;

export interface ReactionSubjectParts {
  namespace: ReactionDomainNamespace;
  targetId: ReactionTargetId;
}

function parseReactionSubjectParts(value: string): ReactionSubjectParts | null {
  const parts = value.split(':');
  if (parts.length !== 3) {
    return null;
  }

  const [namespace, version, targetId] = parts;
  if (version !== REACTION_SUBJECT_KEY_VERSION) {
    return null;
  }

  const namespaceResult = ReactionDomainNamespaceSchema.safeParse(namespace);
  const targetIdResult = ReactionTargetIdSchema.safeParse(targetId);
  if (!namespaceResult.success || !targetIdResult.success) {
    return null;
  }

  return {
    namespace: namespaceResult.data,
    targetId: targetIdResult.data,
  };
}

export const ReactionSubjectKeySchema = z
  .string()
  .max(REACTION_SUBJECT_KEY_MAX_LENGTH)
  .refine((value) => parseReactionSubjectParts(value) !== null, {
    message: 'Invalid reaction subject key',
  })
  .brand<'ReactionSubjectKey'>();
export type ReactionSubjectKey = z.infer<typeof ReactionSubjectKeySchema>;

export function createReactionSubjectKey(
  namespace: string,
  targetId: string,
): ReactionSubjectKey {
  const validNamespace = ReactionDomainNamespaceSchema.parse(namespace);
  const validTargetId = ReactionTargetIdSchema.parse(targetId);

  return ReactionSubjectKeySchema.parse(
    `${validNamespace}:${REACTION_SUBJECT_KEY_VERSION}:${validTargetId}`,
  );
}

export function parseReactionSubjectKey(
  subjectKey: string,
): ReactionSubjectParts {
  const validSubjectKey = ReactionSubjectKeySchema.parse(subjectKey);
  const parts = parseReactionSubjectParts(validSubjectKey);

  if (parts === null) {
    throw new Error('Invalid reaction subject key');
  }

  return parts;
}

export const DesiredReactionStateSchema = z
  .object({
    reactionType: ReactionTypeSchema,
    active: z.boolean(),
    idempotencyKey: z
      .string()
      .trim()
      .min(1, {
        message: 'Idempotency key cannot be blank',
      })
      .max(REACTION_IDEMPOTENCY_KEY_MAX_LENGTH),
  })
  .strict();
export type DesiredReactionState = z.infer<typeof DesiredReactionStateSchema>;

export const ReactionActorDisplayKindSchema = z.enum([
  'person',
  'former-user',
]);
export type ReactionActorDisplayKind = z.infer<
  typeof ReactionActorDisplayKindSchema
>;

export const ReactionActorSchema = z
  .object({
    displayKind: ReactionActorDisplayKindSchema,
    displayLabel: z
      .string()
      .min(1)
      .max(REACTION_ACTOR_DISPLAY_LABEL_MAX_LENGTH)
      .optional(),
    roleLabel: z
      .string()
      .min(1)
      .max(REACTION_ACTOR_CONTEXT_LABEL_MAX_LENGTH)
      .optional(),
    organizationLabel: z
      .string()
      .min(1)
      .max(REACTION_ACTOR_CONTEXT_LABEL_MAX_LENGTH)
      .optional(),
  })
  .strict();
export type ReactionActor = z.infer<typeof ReactionActorSchema>;

export const ReactionGroupSchema = z
  .object({
    reactionType: ReactionTypeSchema,
    count: z.number().int().nonnegative(),
    isCurrentPersonActive: z.boolean(),
    actors: z.array(ReactionActorSchema).max(REACTION_ACTOR_PREVIEW_MAX),
  })
  .strict();
export type ReactionGroup = z.infer<typeof ReactionGroupSchema>;

export const ReactionSetStateResultSchema = z
  .object({
    reactionType: ReactionTypeSchema,
    active: z.boolean(),
    changed: z.boolean(),
    replayed: z.boolean(),
  })
  .strict();
export type ReactionSetStateResult = z.infer<
  typeof ReactionSetStateResultSchema
>;
